import { useTranslation } from "react-i18next";

import { LoadingButton } from "@mui/lab";
import { Typography, styled } from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import axios from "axios";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { password as passwordAPI, metadata, auth } from "marvin-auth-kit";

import { RegistrationArguments } from "../../hooks/usersHooks";
import { useErrorSnackbar } from "../../hooks/errorHooks";

interface Props {}

interface ForgotPasswordConfirmForm {
  password: string;
}

export function PasswordForgotConfirmPage(props: Props) {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const navigate = useNavigate();

  const initialValues: ForgotPasswordConfirmForm = {
    password: "",
  };
  const validationScheme = Yup.object().shape({
    password: Yup.string()
      .min(6, (min) => t("validation.invalidPassword", min))
      .required(t("validation.requiredPassword")),
  });

  async function onSubmit(
    values: ForgotPasswordConfirmForm,
    helpers: FormikHelpers<ForgotPasswordConfirmForm>
  ) {
    const { setSubmitting } = helpers;
    const { password } = values;
    setSubmitting(true);
    try {
      await passwordAPI.confirm(token || "", password);
      auth.applyInterceptors(axios);
      const response = await metadata.get<RegistrationArguments["metadata"]>();
      const config =
        response.lastRegistrationConfig || t("message.undefined_config");
      enqueueSnackbar(t("message.thankYouPasswordtIsReset"), {
        variant: "info",
      });
      return navigate(`/${config}/app/layers`);
    } catch (errors) {
      enqueueErrorSnackbar(errors);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationScheme}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => {
        return (
          <StyledForm>
            <Typography variant="h4" gutterBottom>
              {t("label.changePassword")}
            </Typography>

            <Field
              component={TextField}
              id="password"
              type="password"
              name="password"
              variant="filled"
              label={t("label.password")}
              fullWidth
              autoFocus
              placeholder={t("label.typeNewPassword")}
            />
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmitting}
            >
              {t("label.change")}
            </LoadingButton>
          </StyledForm>
        );
      }}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;

  & > * + * {
    margin-top: ${({ theme }) => `${theme.spacing(3)}!important`};
  }
`;
